import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DeskTable, StackedDateTime, muted, type Column } from '@/components/common/DeskTable'
import ShortId from '@/components/common/ShortId'
import PageFrame from '@/components/common/PageFrame'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { backofficeApi, type BcaRow } from '@/lib/api-client'
import { fieldErrors } from '@/lib/errors'
import { FORM_CARD } from '@/lib/ui-shell'
import { BCA_WIDTH } from '@/lib/column-widths'
import { dash, fmtDate, fmtMoney } from '@/lib/format'
import { useDeskSort } from '@/hooks/useDeskSort'
import { useListPageSize } from '@/hooks/useListPageSize'

const clip = 'overflow-hidden'

const bcaColumns: Column<BcaRow>[] = [
  { key: 'date', sortKey: 'date', sortFirst: 'desc', header: 'Date', width: BCA_WIDTH.date, mobile: 'subtitle', cell: (r) => muted(fmtDate(r.txn_date)) },
  { key: 'amount', sortKey: 'amount', sortFirst: 'desc', header: 'Amount', align: 'right', width: BCA_WIDTH.amount, mobile: 'amount', cell: (r) => muted(fmtMoney(r.amount)) },
  { key: 'desc', sortKey: 'desc', header: 'Description', width: BCA_WIDTH.desc, className: clip, mobile: 'title', cell: (r) => <span className="block truncate" title={r.description || undefined}>{muted(dash(r.description))}</span> },
  { key: 'acct', sortKey: 'acct', header: 'Account', width: BCA_WIDTH.acct, className: clip, mobile: 'field', cell: (r) => <span className="block truncate font-mono" title={r.account_number || undefined}>{muted(dash(r.account_number))}</span> },
  { key: 'matched', header: 'Matched request', headerTitle: 'Fund request this line was matched to', width: BCA_WIDTH.matched, className: clip, mobile: 'field', cell: (r) => <ShortId value={r.matched_request_id} /> },
  { key: 'imported', sortKey: 'imported', sortFirst: 'desc', header: 'Imported', width: BCA_WIDTH.imported, className: 'overflow-hidden whitespace-normal', cell: (r) => <StackedDateTime value={r.imported_at} /> },
]

export function BcaCsvPage() {
  const qc = useQueryClient()
  const [sp, setSp] = useSearchParams()
  const page = Number(sp.get('page') || '1')
  const pageSize = useListPageSize()
  const { sort, onSortChange, sortQuery } = useDeskSort(['date', 'amount', 'desc', 'acct', 'imported'])
  const [csv, setCsv] = useState('')
  const list = useQuery({
    queryKey: ['bca-list', page, pageSize, sortQuery],
    queryFn: () => backofficeApi.bcaList({ page, page_size: pageSize, ...sortQuery }),
  })
  const imp = useMutation({
    mutationFn: () => backofficeApi.importBcaCsv(csv),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bca-list'] })
      setCsv('') // avoid re-importing the same file by accident
    },
  })
  const rowErrors = fieldErrors(imp.error).rows
  const items = list.data?.items ?? []
  return (
    <PageFrame title="BCA Transactions" description="Bank statement lines imported from BCA CSV exports. Matching lines settle client deposits.">
      <div className={`${FORM_CARD} space-y-2`}>
        <Field
          label="Statement CSV"
          hint="First line must be the header: date,amount,description,account. Example row: 2026-10-01,1500000,TRSF E-BANKING CR,1234567890. The whole file is rejected if any row is invalid."
        >
          <Textarea className="min-h-28 font-mono" value={csv} onChange={(e) => setCsv(e.target.value)} />
        </Field>
        <Button onClick={() => imp.mutate()} disabled={imp.isPending || !csv.trim()}>
          Import CSV
        </Button>
        {imp.data ? (
          <p className="text-sm" role="status">
            Imported {imp.data.imported} rows.
            {imp.data.skipped_duplicates ? ` ${imp.data.skipped_duplicates} duplicates skipped.` : ''}
          </p>
        ) : null}
        {imp.error ? (
          <div role="alert" className="space-y-1 text-sm text-destructive">
            <p className="font-semibold">{(imp.error as Error).message}</p>
            {rowErrors.length ? (
              <ul className="max-h-40 list-disc space-y-0.5 overflow-auto pl-5">
                {rowErrors.map((r, i) => (
                  <li key={i}>
                    Row {r.row}, {r.field}: {r.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
      <DeskTable
        columns={bcaColumns}
        rows={items}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        error={list.error}
        emptyTitle="No BCA transactions"
        emptyDescription="Import a statement CSV above."
        sort={sort}
        onSortChange={onSortChange}
        paging={{
          total: list.data?.total ?? 0,
          page,
          pageSize,
          onPage: (p) => {
            const cur = new URLSearchParams(sp)
            cur.set('page', String(p))
            cur.set('page_size', String(pageSize))
            setSp(cur, { replace: true })
          },
          onPageSize: (size) => {
            const cur = new URLSearchParams(sp)
            cur.set('page', '1')
            cur.set('page_size', String(size))
            setSp(cur, { replace: true })
          },
        }}
      />
    </PageFrame>
  )
}
